import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { BookMarked, Plus, X, Trash2, Edit3, Save } from 'lucide-react';
import { getSignedToken } from '../shared/TicketChatComponents';
import { confirmDialog } from '../../lib/confirmStore';

interface CannedReply {
  id: string;
  title: string;
  body: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

interface Props {
  onSelect: (body: string) => void;
}

export default function CannedReplyPicker({ onSelect }: Props) {
  const [open, setOpen] = useState(false);
  const [replies, setReplies] = useState<CannedReply[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<CannedReply | null>(null);
  const [saving, setSaving] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');

  // The panel is PORTALLED to <body> on purpose. As an absolutely-positioned
  // panel inside the composer it was clipped by the chat column's
  // `overflow-hidden` border box — which is why the saved replies appeared
  // "hidden behind the left column" and could not be read.
  const [rect, setRect] = useState<DOMRect | null>(null);
  const anchorRef = React.useRef<HTMLButtonElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setEditing(null);
    setNewTitle('');
    setNewBody('');
  }, []);

  const syncRect = useCallback(() => {
    const el = anchorRef.current;
    if (el) setRect(el.getBoundingClientRect());
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    syncRect();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (anchorRef.current?.contains(t as Node) || popoverRef.current?.contains(t as Node)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('resize', syncRect);
    window.addEventListener('scroll', syncRect, true);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', syncRect);
      window.removeEventListener('scroll', syncRect, true);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, syncRect, close]);

  const fetchReplies = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getSignedToken();
      const res = await fetch('/api/canned-replies', {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await res.json();
      setReplies(data.replies || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { if (open) fetchReplies(); }, [open, fetchReplies]);

  const handleSelect = (body: string) => {
    onSelect(body);
    setOpen(false);
  };

  const startCreate = () => {
    setEditing({ id: '', title: '', body: '', userId: '', createdAt: '', updatedAt: '' });
    setNewTitle('');
    setNewBody('');
  };

  const startEdit = (r: CannedReply) => {
    setEditing(r);
    setNewTitle(r.title);
    setNewBody(r.body);
  };

  const cancelEdit = () => {
    setEditing(null);
    setNewTitle('');
    setNewBody('');
  };

  const handleSave = async () => {
    if (!newTitle.trim() || !newBody.trim()) return;
    setSaving(true);
    try {
      const token = await getSignedToken();
      const isNew = !editing!.id;
      const url = isNew ? '/api/canned-replies' : `/api/canned-replies/${editing!.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ title: newTitle.trim(), body: newBody.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        if (isNew && data.reply) {
          setReplies(prev => [...prev, data.reply]);
        } else {
          setReplies(prev => prev.map(r => r.id === editing!.id ? { ...r, title: newTitle.trim(), body: newBody.trim() } : r));
        }
        cancelEdit();
      }
    } catch {}
    setSaving(false);
  };

  const handleDelete = async (id: string, title?: string) => {
    if (!(await confirmDialog(`Delete the canned reply "${title || 'this reply'}"? This cannot be undone.`, { confirmLabel: 'Delete' }))) return;
    try {
      const token = await getSignedToken();
      const res = await fetch(`/api/canned-replies/${id}`, {
        method: 'DELETE',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.ok) setReplies(prev => prev.filter(r => r.id !== id));
    } catch {}
  };

  return (
    <div className="relative">
      <button
        ref={anchorRef}
        onClick={() => { if (open) close(); else setOpen(true); }}
        className="shrink-0 p-2 text-slate-400 hover:text-brand bg-brand-active rounded-full transition-all cursor-pointer min-w-[40px] min-h-[40px] flex items-center justify-center"
        type="button"
        title="Canned replies"
        aria-label="Open canned replies"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <BookMarked className="h-4 w-4" aria-hidden="true" />
      </button>

      {open && rect && ReactDOM.createPortal(
        <div
          ref={popoverRef}
          role="dialog"
          aria-label="Canned replies"
          className="fixed z-[9300] flex max-h-[min(70vh,460px)] w-[min(92vw,360px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
          style={{
            // Opens ABOVE the composer, clamped to the viewport. Portalled to
            // <body> so the chat column's `overflow-hidden` can never clip it and
            // the ticket list can never paint over it.
            bottom: Math.max(8, window.innerHeight - rect.top + 8),
            left: Math.max(8, Math.min(rect.right - 360, window.innerWidth - 368)),
          }}
        >
            {/* Header */}
            <div className="shrink-0 px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-blue-50/50 to-white">
              <div className="flex items-center gap-2">
                <BookMarked className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-extrabold text-slate-800">Canned Replies</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={editing ? cancelEdit : startCreate}
                  className="p-1.5 text-slate-400 hover:text-brand bg-brand-active rounded-lg transition-all cursor-pointer min-w-[32px] min-h-[32px] flex items-center justify-center"
                  type="button"
                  title={editing ? 'Cancel' : 'Add new'}
                >
                  {editing ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>

            {/* Edit form */}
            {editing && (
              <div className="shrink-0 px-3 py-2.5 border-b border-blue-100 bg-blue-50/30 space-y-2">
                <input
                  type="text"
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                  placeholder="Short title..."
                  className="w-full border border-slate-200 rounded-lg px-2.5 py-2 text-xs outline-none focus:border-blue-400 bg-white"
                />
                <textarea
                  value={newBody}
                  onChange={e => setNewBody(e.target.value)}
                  placeholder="Reply text..."
                  className="w-full border border-slate-200 rounded-lg px-2.5 py-2 text-xs outline-none focus:border-blue-400 bg-white resize-none min-h-[60px]"
                  rows={2}
                />
                <div className="flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={!newTitle.trim() || !newBody.trim() || saving}
                    className="flex items-center gap-1 px-3 py-1.5 btn-primary  disabled:bg-slate-300 text-white text-[10px] font-bold rounded-lg transition-all cursor-pointer disabled:cursor-not-allowed"
                    type="button"
                  >
                    <Save className="h-3 w-3" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            )}

            {/* List */}
            <div className="flex-1 overflow-y-auto">
              {loading && (
                <div className="px-4 py-8 text-center">
                  <div className="h-4 w-4 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              )}
              {!loading && replies.length === 0 && (
                <div className="px-4 py-8 text-center text-xs text-slate-500">
                  No saved replies yet. Click + to add one.
                </div>
              )}
              {!loading && replies.map(r => (
                <div
                  key={r.id}
                  className="group px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-slate-700 truncate">{r.title}</p>
                      <p className="text-[11px] text-slate-500 truncate mt-0.5">{r.body}</p>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => startEdit(r)}
                        className="p-1 text-slate-300 hover:text-blue-500 rounded transition-colors cursor-pointer"
                        type="button"
                        title="Edit"
                      >
                        <Edit3 className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => handleDelete(r.id, r.title)}
                        className="p-1 text-slate-300 hover:text-red-500 rounded transition-colors cursor-pointer"
                        type="button"
                        title="Delete"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => handleSelect(r.body)}
                    className="mt-1 text-[10px] font-bold text-brand hover:text-brand cursor-pointer"
                    type="button"
                  >
                    Insert →
                  </button>
                </div>
              ))}
            </div>
          </div>
        ,
        document.body,
      )}
    </div>
  );
}
